/** Helpers to read AI SDK tool parts, whether static (`tool-<name>`) or dynamic (MCP tools). */
export interface ToolPartLike {
  type: string;
  toolName?: string;
  toolCallId?: string;
  state: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  approval?: { id: string; approved?: boolean; reason?: string };
}

export const isToolPart = (p: { type: string }) => p.type.startsWith("tool-") || p.type === "dynamic-tool";

export const toolNameOf = (p: ToolPartLike) => (p.type === "dynamic-tool" ? (p.toolName ?? "tool") : p.type.replace(/^tool-/, ""));

/** MCP results arrive as CallToolResult ({ content, structuredContent, isError }); local tools as plain objects. */
export function unwrapOutput(output: unknown): { data: Record<string, unknown> | null; isError: boolean } {
  if (!output || typeof output !== "object") return { data: null, isError: false };
  const o = output as { structuredContent?: Record<string, unknown>; content?: Array<{ type: string; text?: string }>; isError?: boolean };
  if ("structuredContent" in o || "content" in o) {
    let data = o.structuredContent ?? null;
    if (!data) {
      const text = o.content?.find((c) => c.type === "text")?.text;
      try {
        data = text ? (JSON.parse(text) as Record<string, unknown>) : null;
      } catch {
        data = text ? { message: text } : null;
      }
    }
    return { data, isError: Boolean(o.isError) || Boolean(data && "error" in data) };
  }
  const plain = output as Record<string, unknown>;
  return { data: plain, isError: "error" in plain };
}

export const errorMessage = (data: Record<string, unknown> | null) => {
  const e = data?.error as { message?: string } | string | undefined;
  return typeof e === "string" ? e : (e?.message ?? "Error al consultar los datos.");
};

export const TOOL_LABELS: Record<string, string> = {
  get_business_context: "Contexto del negocio",
  get_sales_summary: "Resumen de ventas",
  get_sales_breakdown: "Ventas por dimensión",
  get_visits_breakdown: "Visitas con tarjeta",
  get_location_health: "Salud por sucursal",
  get_products: "Catálogo y márgenes",
  get_customer_segments: "Segmentos de clientes",
  find_customers: "Búsqueda de clientes",
  get_campaign_history: "Campañas pasadas",
  preview_audience: "Alcance real",
  evaluate_campaign: "Evaluación de campaña",
  create_campaign_draft: "Borrador de campaña",
  show_chart: "Gráfica",
  show_table: "Tabla",
  show_kpis: "Indicadores",
};
